import { computed, inject, Injectable, signal } from '@angular/core';
import { UserService } from '../services/api-services/user.service';
import { ChatExpert, ChatSession, ChatSessionFile, Message } from './chat.model';

/** Holds chat view state and persists user-specific selections and replay cursors. */
@Injectable({ providedIn: 'root' })
export class ChatStore {
  /** Services. */
  private readonly userService = inject(UserService);

  /** State. */
  readonly experts = signal<ChatExpert[]>([]);
  readonly selectedExpert = signal<ChatExpert | null>(null);
  readonly activeSession = signal<ChatSession | null>(null);
  readonly messages = signal<Message[]>([]);
  readonly files = signal<ChatSessionFile[]>([]);
  readonly currentAgentMsg = signal<Message | null>(null);
  readonly error = signal('');
  readonly loading = signal(true);
  readonly creating = signal(false);
  readonly isRunPending = signal(false);
  readonly cancelling = signal(false);

  /** Whether no conversation is currently selected. */
  readonly isOverview = computed(
    () => this.activeSession() === null && this.messages().length === 0
  );

  /** Loads accessible experts and restores the new-chat default. */
  setExperts(experts: ChatExpert[]): void {
    this.experts.set(experts.filter(expert => expert.status === 'active'));
    this.selectedExpert.set(this.storedExpert() ?? this.experts()[0] ?? null);
  }

  /** Chooses the expert that will answer the next message. */
  selectExpert(expertId: string): void {
    const expert = this.experts().find(item => item.id === expertId);
    if (!expert || this.selectedExpert()?.id === expert.id) return;
    this.selectedExpert.set(expert);
    this.persistExpert(expert.id, this.activeSession()?.id);
  }

  /** Selects a saved conversation and restores its expert choice. */
  openSession(session: ChatSession): void {
    this.activeSession.set(session);
    const expert =
      this.storedExpert(session.id) ?? this.experts().find(item => item.id === session.expertId);
    this.selectedExpert.set(expert ?? null);
    if (expert) this.persistExpert(expert.id, session.id);
    this.persistSession(session.id);
    this.messages.set([]);
    this.files.set([]);
    this.currentAgentMsg.set(null);
    this.isRunPending.set(false);
    this.cancelling.set(false);
    this.error.set('');
  }

  /** Records a newly created conversation without changing its expert. */
  startSession(session: ChatSession): void {
    this.activeSession.set(session);
    this.persistSession(session.id);
    const expert = this.selectedExpert();
    if (expert) this.persistExpert(expert.id, session.id);
  }

  /** Returns to the overview while keeping the selected expert. */
  clearSession(): void {
    this.activeSession.set(null);
    this.persistSession(null);
    this.messages.set([]);
    this.files.set([]);
    this.currentAgentMsg.set(null);
    this.isRunPending.set(false);
    this.cancelling.set(false);
    this.error.set('');
  }

  /** Reads the saved conversation selection for this user. */
  selectedSessionId(): string | null {
    const key = this.storageKey('selectedSession');
    return key ? localStorage.getItem(key) : null;
  }

  /** Removes saved state for a deleted or inaccessible conversation. */
  forgetSession(sessionId: string): void {
    if (this.selectedSessionId() === sessionId) this.persistSession(null);
    for (const kind of ['selectedExpert', 'cursor'] as const) {
      const key = this.storageKey(kind, sessionId);
      if (key) localStorage.removeItem(key);
    }
  }

  /** Reads the last event row processed for this conversation. */
  savedCursor(sessionId: string): string | undefined {
    const key = this.storageKey('cursor', sessionId);
    return key ? (localStorage.getItem(key) ?? undefined) : undefined;
  }

  /** Saves the last event row processed for this conversation. */
  saveCursor(sessionId: string, cursorId: string): void {
    const key = this.storageKey('cursor', sessionId);
    if (key) localStorage.setItem(key, cursorId);
  }

  /** Builds a storage key scoped to the signed-in user. */
  private storageKey(kind: string, sessionId?: string): string | null {
    const userId = this.userService.user()?.userId;
    if (userId == null) return null;
    const baseKey = `chat.${kind}.${userId}`;
    return sessionId ? `${baseKey}.${sessionId}` : baseKey;
  }

  /** Restores an expert only while it remains available to this user. */
  private storedExpert(sessionId?: string): ChatExpert | undefined {
    const key = this.storageKey('selectedExpert', sessionId);
    if (!key) return undefined;
    const expertId = localStorage.getItem(key);
    if (!expertId) return undefined;
    const expert = this.experts().find(item => item.id === expertId);
    if (!expert) localStorage.removeItem(key);
    return expert;
  }

  /** Saves the new-chat default and optionally one conversation's expert. */
  private persistExpert(expertId: string, sessionId?: string): void {
    const defaultKey = this.storageKey('selectedExpert');
    if (defaultKey) localStorage.setItem(defaultKey, expertId);
    if (!sessionId) return;
    const sessionKey = this.storageKey('selectedExpert', sessionId);
    if (sessionKey) localStorage.setItem(sessionKey, expertId);
  }

  /** Saves or clears the selected conversation ID. */
  private persistSession(sessionId: string | null): void {
    const key = this.storageKey('selectedSession');
    if (!key) return;
    if (sessionId) localStorage.setItem(key, sessionId);
    else localStorage.removeItem(key);
  }
}
